import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, FileSearch, MessagesSquare, Users, CreditCard, FileText,
  Mail, Building2, LogOut, Menu, X, Settings as SettingsIcon, Zap, Sparkles, Star, HardHat,
  Trees, BadgePoundSterling, Inbox, Search, ChevronDown, Plus, Home as HomeIcon,
  ClipboardCheck,
} from "lucide-react";
import { getAdminKey, setAdminKey } from "@/lib/adminApi";

/* Grouped navigation — gives the sidebar a clear hierarchy rather than
   one long flat list. Each section gets a subtle eyebrow label. */
const NAV_SECTIONS = [
  {
    label: "Operations",
    items: [
      { to: "/adminpanel",                 label: "Command Centre",  icon: <LayoutDashboard size={15}/>, end: true },
      { to: "/adminpanel/operator-overview", label: "Operator Overview", icon: <Building2 size={15}/> },
      { to: "/adminpanel/quote-reviews",   label: "Quote Reviews",   icon: <FileSearch size={15}/> },
      { to: "/adminpanel/support-cases",   label: "Support Cases",   icon: <MessagesSquare size={15}/> },
      { to: "/adminpanel/contractors",     label: "Contractors",     icon: <HardHat size={15}/> },
      { to: "/adminpanel/comms",           label: "Comms Hub",       icon: <Inbox size={15}/> },
      { to: "/adminpanel/automation",      label: "Automation",      icon: <Zap size={15}/> },
    ],
  },
  {
    label: "Assets",
    items: [
      { to: "/adminpanel/assets/parks",    label: "Parks",           icon: <Trees size={15}/> },
      { to: "/adminpanel/assets/plots",    label: "Plots",           icon: <HomeIcon size={15}/> },
    ],
  },
  {
    label: "People & Leads",
    items: [
      { to: "/adminpanel/members",         label: "Members",         icon: <Users size={15}/> },
      { to: "/adminpanel/health-checks",   label: "Health Checks",   icon: <ClipboardCheck size={15}/> },
      { to: "/adminpanel/seo-leads",       label: "SEO Leads",       icon: <Sparkles size={15}/> },
      { to: "/adminpanel/reviews",         label: "Reviews",         icon: <Star size={15}/> },
      { to: "/adminpanel/contact",         label: "Contact Inbox",   icon: <Mail size={15}/> },
      { to: "/adminpanel/operators",       label: "Operator Pilots", icon: <Building2 size={15}/> },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/adminpanel/margin",              label: "Margin Dashboard",  icon: <BadgePoundSterling size={15}/> },
      { to: "/adminpanel/payments",            label: "Customer Payments", icon: <CreditCard size={15}/> },
      { to: "/adminpanel/invoices",            label: "Invoices",          icon: <FileText size={15}/> },
      { to: "/adminpanel/contractor-payments", label: "Contractor Pay",    icon: <HardHat size={15}/> },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { to: "/adminpanel/intel/operators",    label: "Operators",     icon: <Building2 size={15}/> },
      { to: "/adminpanel/intel/parks",        label: "Parks",         icon: <Trees size={15}/> },
      { to: "/adminpanel/intel/repair-costs", label: "Repair Costs",  icon: <BadgePoundSterling size={15}/> },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/adminpanel/settings",        label: "Settings",        icon: <SettingsIcon size={15}/> },
    ],
  },
];

const QUICK_ACTIONS = [
  { to: "/adminpanel/quote-reviews", label: "Open quote reviews",  icon: <FileSearch size={14}/> },
  { to: "/adminpanel/invoices",      label: "Create invoice",      icon: <FileText size={14}/> },
  { to: "/adminpanel/comms",         label: "Browse communications", icon: <Inbox size={14}/> },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [hasKey, setHasKey] = useState(() => !!getAdminKey());

  useEffect(() => {
    if (!hasKey) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/adminpanel/login?next=${next}`, { replace: true });
    }
  }, [hasKey, location, navigate]);

  // Close quick actions popover on route change
  useEffect(() => { setQuickOpen(false); setOpen(false); }, [location.pathname]);

  if (!hasKey) return null;

  const logout = () => {
    setAdminKey("");
    navigate("/adminpanel/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F4ED] flex">
      {/* ---------- SIDEBAR ---------- */}
      <aside
        className={`bg-[#071A33] text-white w-64 shrink-0 flex flex-col ${open ? "fixed inset-y-0 left-0 z-40" : "hidden"} lg:flex lg:sticky lg:top-0 lg:h-screen`}
        data-testid="admin-sidebar"
      >
        <div className="px-5 pt-6 pb-5 border-b border-white/[0.08] flex items-start justify-between">
          <Link to="/adminpanel" className="flex items-start gap-3 group" data-testid="sidebar-brand">
            <img src="/images/logo-dark.png" alt="PHS" className="h-9 w-auto mt-0.5"/>
            <div className="leading-tight">
              <p className="font-display text-[15px] tracking-tight text-white">Park Home Support</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#B88A2A] mt-0.5">Operations Command Centre</p>
            </div>
          </Link>
          <button className="lg:hidden text-white/80" onClick={() => setOpen(false)}><X size={20}/></button>
        </div>

        <nav className="px-3 pt-4 pb-6 overflow-y-auto flex-1">
          {NAV_SECTIONS.map((sec) => (
            <div key={sec.label} className="mb-5">
              <p className="px-3 text-[9.5px] uppercase tracking-[0.22em] text-white/30 font-medium mb-1.5">{sec.label}</p>
              <div className="flex flex-col gap-0.5">
                {sec.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-md transition ${
                        isActive
                          ? "bg-[#064E2E] text-white shadow-[inset_2px_0_0_#B88A2A]"
                          : "text-white/65 hover:bg-white/5 hover:text-white"
                      }`
                    }
                    data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {n.icon} {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-white/[0.08]">
          <button onClick={logout} className="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-white/60 hover:bg-white/5 hover:text-white rounded-md" data-testid="admin-logout-btn">
            <LogOut size={15}/> Sign out
          </button>
        </div>
      </aside>

      {/* ---------- MAIN COLUMN ---------- */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* TOP BAR */}
        <header className="bg-white border-b border-[#E2DCD0] sticky top-0 z-30" data-testid="admin-topbar">
          <div className="h-14 px-4 lg:px-8 flex items-center gap-3">
            <button className="lg:hidden text-[#475569]" onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu size={20}/></button>

            {/* Search */}
            <form
              onSubmit={(e) => { e.preventDefault(); const q = e.currentTarget.elements.q.value.trim(); if (q) navigate(`/adminpanel/comms?q=${encodeURIComponent(q)}`); }}
              className="flex-1 max-w-2xl relative"
            >
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"/>
              <input
                name="q"
                placeholder="Search jobs, parks, residents, contractors…"
                className="w-full pl-9 pr-3 h-9 text-sm bg-[#F7F4ED] border border-transparent hover:border-[#E2DCD0] focus:border-[#064E2E] focus:bg-white rounded-md outline-none transition"
                data-testid="topbar-search"
              />
            </form>

            {/* Quick Actions */}
            <div className="relative">
              <button
                onClick={() => setQuickOpen((v) => !v)}
                className="h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#071A33] hover:bg-[#F7F4ED] rounded-md border border-[#E2DCD0]"
                data-testid="topbar-quick-actions"
              >
                <Plus size={13}/> <span className="hidden sm:inline">Quick Actions</span>
                <ChevronDown size={12} className={`transition ${quickOpen ? "rotate-180" : ""}`}/>
              </button>
              {quickOpen && (
                <div className="absolute right-0 top-11 w-60 bg-white border border-[#E2DCD0] rounded-md shadow-lg overflow-hidden z-40">
                  {QUICK_ACTIONS.map((a) => (
                    <Link key={a.to} to={a.to} className="flex items-center gap-2.5 px-3 py-2 text-sm text-[#071A33] hover:bg-[#F7F4ED]">
                      {a.icon} {a.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Operator identity */}
            <div className="hidden md:flex items-center gap-2.5 pl-3 ml-1 border-l border-[#E2DCD0]" data-testid="topbar-identity">
              <span className="w-8 h-8 rounded-full bg-[#064E2E] text-white text-xs font-semibold flex items-center justify-center">OP</span>
              <div className="leading-tight">
                <p className="text-[13px] font-medium text-[#071A33]">Operator</p>
                <p className="text-[10px] text-[#B88A2A] uppercase tracking-[0.12em]">Admin</p>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 flex-1">
          <Outlet/>
        </main>
      </div>
    </div>
  );
}
